import React, { useState, useEffect } from 'react';
import { ArrowRight, Shield, Gem, Droplets, Clock, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { submitEmail, getLeadCount } from '../services/api';
import axios from 'axios';
import '../styles/landing.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const faqData = [
  { id: 1, question: "What is the Ronda R01-01 calibre?", answer: "The Ronda R01-01 is an entirely new high-end Swiss automatic calibre launched by Ronda in June 2026 to mark their 80th anniversary. It features an 80-hour power reserve, 25 jewels, full silicon escapement and hairspring, free-sprung balance, and 200+ gauss magnetic resistance." },
  { id: 2, question: "What dial options are available?", answer: "The R Automatic is available in two stunning dials: Ocean Blue and Lunar Sand." },
  { id: 3, question: "What does the $20 deposit secure?", answer: "The $20 deposit reserves your spot and guarantees you priority access to the R Automatic at the 50% introductory price of $649.50 (retail $1,299). It's fully refundable if you change your mind." },
  { id: 4, question: "Can I pay the full amount now?", answer: "Yes! You can pay the full introductory price of $649.50 (50% off retail $1,299) to secure your watch immediately. This locks in the discounted price and puts you at the front of the production queue." },
  { id: 5, question: "What are the case specifications?", answer: "40 mm case in 316L stainless steel, sapphire crystal, 300 m (30 ATM) water resistance. The movement features D3 premium finishing with rhodium-plated bridges, Côtes de Genève, and colimaçonnage." },
  { id: 6, question: "What is the Custom Dial option?", answer: "For $1,649.50 ($649.50 watch + $1,000 custom dial), we create a completely bespoke dial for your R Automatic — your own unique design, one of a kind. You'll work directly with our design team to craft a dial that's exclusively yours. No two custom dials are alike." },
  { id: 7, question: "When will the watch be delivered?", answer: "The expected delivery for the R Automatic is April 2027. We will keep you updated on production milestones and shipping timelines throughout the process." },
  { id: 7, question: "Is my payment secure?", answer: "All payments are processed through Stripe with 256-bit SSL encryption. Your financial information is never stored on our servers." },
];

const LandingPage = () => {
  const [selectedPlan, setSelectedPlan] = useState('deposit');
  const [isProcessing, setIsProcessing] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyName, setNotifyName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { getLeadCount(); }, []);

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Please enter your email'); return; }
    setIsProcessing(true);
    const lookupKey = selectedPlan === 'custom' ? 'r_automatic_custom_1649' : selectedPlan === 'full' ? 'r_automatic_full_649' : 'r_series_prebook_20';
    try {
      const { data } = await axios.post(`${API_URL}/api/payments/checkout`, {
        lookup_key: lookupKey, quantity: 1,
        origin_url: window.location.origin,
        email, name,
      });
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error('Failed to start checkout. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!notifyEmail || !notifyName) { toast.error('Please fill in all fields'); return; }
    setIsSubmitting(true);
    try {
      const result = await submitEmail(notifyEmail, notifyName);
      if (result.success) { toast.success(result.message); setNotifyEmail(''); setNotifyName(''); }
    } catch (error) { toast.error(error.message || 'Something went wrong.'); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="dark-header">
        <div className="logo-text">ECLAT GROVE<sup className="tm-mark">TM</sup></div>
        <nav className="dark-nav desktop-only">
          <a href="#watch" className="dark-nav-link">The Watch</a>
          <a href="#specs" className="dark-nav-link">Specs</a>
          <a href="#faq" className="dark-nav-link">FAQ</a>
          <a href="#reserve">
            <Button className="btn-prebook-nav" data-testid="header-reserve-btn">Reserve Now</Button>
          </a>
        </nav>
      </header>

      {/* Hero */}
      <section className="hero-section" style={{ background: 'linear-gradient(160deg, #0a0b0d 0%, #0f1114 40%, #0d0e11 100%)' }}>
        <div className="hero-content">
          <div className="hero-text">
            <div className="brand-badge body-small">ECLAT GROVE<sup className="tm-mark">TM</sup> R-SERIES</div>
            <div className="hero-product-name">R Automatic</div>
            <div className="hero-slogan">Swiss Ronda R01-01 (D3) Calibre</div>
            <p className="body-large hero-subheadline">
              Be the first to wear this watch on the planet with the automatic Swiss R01-01 calibre.
            </p>
            <div className="hero-pricing">
              <span className="hero-price-old">$1,299</span>
              <span className="hero-price-new">$649.50</span>
              <span className="hero-price-tag">50% OFF — Introductory Price</span>
            </div>
            <div className="hero-cta-group">
              <Button className="btn-prebook-hero" data-testid="hero-reserve-btn"
                onClick={() => document.getElementById('reserve')?.scrollIntoView({ behavior: 'smooth' })}>
                Reserve Yours
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </div>
            <ChevronDown className="scroll-indicator" size={32} />
          </div>
        </div>
      </section>

      {/* Feature Strip */}
      <section className="feature-strip">
        <div className="feature-item"><Clock size={18} /> <span>80-Hour Power Reserve</span></div>
        <div className="feature-item"><Shield size={18} /> <span>316L Steel — 40 mm</span></div>
        <div className="feature-item"><Droplets size={18} /> <span>300 m Water Resistant</span></div>
        <div className="feature-item"><Gem size={18} /> <span>Sapphire Crystal</span></div>
      </section>

      {/* Product Showcase */}
      <section id="watch" className="product-showcase">
        <div className="content-container">
          <div className="showcase-layout">
            <div className="showcase-gallery">
              <img
                src="https://customer-assets-agu9un31.emergentagent.net/job_c1358af8-4bc5-49f0-846d-970c2c26ac1b/artifacts/8f8ee03da35cad0a_Eclat%20Grove%20Watches%20at%20Sunset%20%282%29.png"
                alt="ECLAT GROVE R Automatic — Ocean Blue & Lunar Sand"
                className="showcase-main-image" />
            </div>
            <div className="showcase-info">
              <p className="showcase-intro">
                Swiss movement maker Ronda has been building watch movements since 1946. In June 2026, it marked its 80th anniversary by launching the R01 — an entirely new high-end mechanical calibre.
              </p>
              <p className="body-small" style={{ color: 'var(--accent)', marginTop: '0.75rem', marginBottom: '2rem', opacity: 0.8 }}>
                Read more: <a href="https://www.europastar.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>Europa Star</a> & <a href="https://www.wornandwound.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>Worn & Wound</a>
              </p>

              <h3 className="movement-title">Movement Highlights</h3>
              <div className="spec-highlights">
                <div className="spec-highlight"><div className="spec-hl-label">80-hour power reserve</div><div className="spec-hl-desc">Take it off on Friday, still running on Monday</div></div>
                <div className="spec-highlight"><div className="spec-hl-label">25 jewels — 25,200 vph (3.5 Hz)</div><div className="spec-hl-desc">High-precision Swiss automatic movement</div></div>
                <div className="spec-highlight"><div className="spec-hl-label">Full silicon escapement & hairspring</div><div className="spec-hl-desc">Low friction, magnetic resistance, thermal stability</div></div>
                <div className="spec-highlight"><div className="spec-hl-label">Free-sprung balance</div><div className="spec-hl-desc">Precision adjustment screws for exceptional performance</div></div>
                <div className="spec-highlight"><div className="spec-hl-label">200+ gauss magnetic resistance</div><div className="spec-hl-desc">Protection against everyday interference</div></div>
                <div className="spec-highlight"><div className="spec-hl-label">D3 premium finishing</div><div className="spec-hl-desc">Rhodium-plated bridges, Côtes de Genève, colimaçonnage</div></div>
              </div>
              <div className="dial-options" style={{ marginTop: '1.5rem' }}>Available Dials: <span className="dial-accent">Ocean Blue</span> & <span className="dial-accent">Lunar Sand</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Specs Grid */}
      <section id="specs" className="specs-section">
        <div className="content-container">
          <h2 className="display-large section-title">Built for Endurance</h2>
          <div className="specs-grid-compact">
            <div className="spec-card"><h3 className="heading-3">Case</h3><p className="spec-value">40 mm</p><p className="body-small spec-description">316L Stainless Steel</p></div>
            <div className="spec-card"><h3 className="heading-3">Crystal</h3><p className="spec-value">Sapphire</p><p className="body-small spec-description">Scratch resistant</p></div>
            <div className="spec-card"><h3 className="heading-3">Water Resistance</h3><p className="spec-value">300 m / 1000 ft</p><p className="body-small spec-description">30 ATM</p></div>
            <div className="spec-card"><h3 className="heading-3">Movement</h3><p className="spec-value">Swiss Ronda</p><p className="body-small spec-description">R01-01 (D3) Automatic</p></div>
            <div className="spec-card"><h3 className="heading-3">Power Reserve</h3><p className="spec-value">80 Hours</p><p className="body-small spec-description">25 jewels, 3.5 Hz</p></div>
            <div className="spec-card"><h3 className="heading-3">Magnetic Resistance</h3><p className="spec-value">200+ Gauss</p><p className="body-small spec-description">Silicon escapement</p></div>
          </div>
        </div>
      </section>

      {/* Reservation Section */}
      <section id="reserve" className="reserve-section">
        <div className="content-container">
          <div className="reserve-layout">
            <div className="reserve-header">
              <div className="reserve-badge">50% OFF — INTRODUCTORY PRICE</div>
              <h2 className="reserve-title">Reserve Your R Automatic</h2>
              <p className="reserve-subtitle">Be the first to wear this watch on the planet</p>
            </div>

            {/* Plan Selector */}
            <div className="plan-selector">
              <div className={`plan-card ${selectedPlan === 'deposit' ? 'plan-active' : ''}`}
                onClick={() => setSelectedPlan('deposit')} data-testid="plan-deposit">
                <div className="plan-radio">{selectedPlan === 'deposit' && <div className="plan-radio-dot" />}</div>
                <div className="plan-info">
                  <div className="plan-name">$20 Deposit</div>
                  <div className="plan-desc">Reserve your spot — fully refundable</div>
                </div>
                <div className="plan-price">$20</div>
              </div>
              <div className={`plan-card ${selectedPlan === 'full' ? 'plan-active' : ''}`}
                onClick={() => setSelectedPlan('full')} data-testid="plan-full">
                <div className="plan-radio">{selectedPlan === 'full' && <div className="plan-radio-dot" />}</div>
                <div className="plan-info">
                  <div className="plan-name">Pay in Full</div>
                  <div className="plan-desc">Lock in 50% off — priority production</div>
                </div>
                <div className="plan-price">
                  <span className="plan-price-old">$1,299</span>
                  <span>$649.50</span>
                </div>
              </div>
              <div className={`plan-card plan-custom ${selectedPlan === 'custom' ? 'plan-active' : ''}`}
                onClick={() => setSelectedPlan('custom')} data-testid="plan-custom">
                <div className="plan-radio">{selectedPlan === 'custom' && <div className="plan-radio-dot" />}</div>
                <div className="plan-info">
                  <div className="plan-name">Custom Dial + Watch</div>
                  <div className="plan-desc">Your own unique dial design — $649.50 watch + $1,000 custom dial</div>
                </div>
                <div className="plan-price">$1,649.50</div>
              </div>
            </div>

            {/* Checkout Form */}
            <form onSubmit={handleCheckout} className="reserve-form">
              <div className="reserve-form-row">
                <Input type="text" placeholder="Your Name" value={name} onChange={(e) => setName(e.target.value)}
                  className="form-input" data-testid="reserve-name-input" />
                <Input type="email" placeholder="Your Email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="form-input" data-testid="reserve-email-input" required />
              </div>
              <Button type="submit" className="btn-reserve-main" data-testid="reserve-submit-btn" disabled={isProcessing}>
                {isProcessing ? 'Redirecting to checkout...' :
                  selectedPlan === 'custom' ? 'Pay $1,649.50 — Watch + Custom Dial' :
                  selectedPlan === 'full' ? 'Pay $649.50 — Reserve Now' : 'Pay $20 Deposit — Reserve Now'}
                <ArrowRight className="ml-2" size={20} />
              </Button>
              <p className="reserve-secure">Secure payment via Stripe. 256-bit SSL encrypted.</p>
            </form>
          </div>
        </div>
      </section>

      {/* Email List */}
      <section id="notify" className="email-capture-section">
        <div className="content-container">
          <div className="capture-content">
            <h2 className="display-large">Stay in the Loop</h2>
            <p className="body-large capture-subtitle">Not ready to reserve? Get notified for updates.</p>
            <form onSubmit={handleEmailSubmit} className="capture-form">
              <div className="form-row">
                <Input type="text" placeholder="Your Name" value={notifyName} onChange={(e) => setNotifyName(e.target.value)}
                  className="form-input" data-testid="email-name-input" disabled={isSubmitting} />
                <Input type="email" placeholder="Your Email" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)}
                  className="form-input" data-testid="email-email-input" disabled={isSubmitting} />
              </div>
              <Button type="submit" className="btn-primary btn-large btn-full" data-testid="email-submit-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Registering...' : 'Join Email List'}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="faq-section">
        <div className="content-container">
          <h2 className="display-large section-title">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="faq-accordion">
            {faqData.map((faq) =>
              <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                <AccordionTrigger className="faq-question heading-3">{faq.question}</AccordionTrigger>
                <AccordionContent className="faq-answer body-medium">{faq.answer}</AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="content-container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="logo-text">ECLAT GROVE<sup className="tm-mark">TM</sup></div>
              <p className="body-small footer-model">R Automatic — Swiss Ronda R01-01 (D3)</p>
            </div>
            <div className="footer-links">
              <a href="#watch" className="footer-link body-small">The Watch</a>
              <a href="#specs" className="footer-link body-small">Specifications</a>
              <a href="#reserve" className="footer-link body-small">Reserve</a>
              <a href="#faq" className="footer-link body-small">FAQ</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="body-small" style={{ color: 'rgba(255,255,255,0.5)', lineHeight: '1.6', marginBottom: '1rem', maxWidth: '700px' }}>Our Broader Environmental Inspiration: ECLAT GROVE<sup className="tm-mark">TM</sup> was created at the intersection of scientific precision and purposeful design. In 2025, we planted 10,000 trees with Eden: People + Planet (NGO based in California) as a separate brand initiative — fully funded by ECLAT GROVE<sup className="tm-mark">TM</sup> independent of crowdfunding. The funds raised here support watch production and delivery only, not the tree-planting initiative.</p>
            <p className="body-small">© 2026 ECLAT GROVE<sup className="tm-mark">TM</sup>. All rights reserved.</p>
            <p className="body-small">ECLAT GROVE<sup className="tm-mark">TM</sup> BRAND OWNED BY LE GRANDE TECHY LLC, DOVER 19901, DELAWARE, US</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
